import React, { useMemo } from 'react';
import { useTable, useSortBy, Column, ColumnInstance, UseSortByColumnProps, HeaderGroup } from 'react-table';
import './../../components/data-tables/DataTable.scss';
import './last-five-shutdown.scss';
import { useAppSelector } from '../../hooks/storeHooks';
import { formatDate } from '../../utilities/dateHelpers';

interface ShutDownModel {
  date: string;
  dateMilliseconds: number;
  dateLocal: string;
  dateLocalString: string | null;
  dateLocalMilliseconds: number | null;
  endDate: string;
  endDateLocal: string;
  endDateMilliseconds: number;
  endDateLocalString: string | null;
  endDateLocalMilliseconds: number | null;
  isActiveDowntime: boolean;
  offlineHours: number;
  hours: number;
  currentHours: number;
  lostWater?: number;
  code?: string;
  category?: string;
  subCategory?: string;
  comment?: string;
}

interface TaskDataTableProps {
  data: ShutDownModel[];
}

const LastFiveShutdownTable: React.FC<TaskDataTableProps> = ({ data }) => {
  const lastFive = useMemo(() => data, [data]);
  const chartType = useAppSelector((state) => state.AssetAnalysis.chartType);
  const isGLDashboard = chartType?.toLowerCase().includes('gl');

  const columns = useMemo<Column<ShutDownModel>[]>(
    () => [
      {
        Header: isGLDashboard ? 'Shutdown reason' : 'VSD fault',
        accessor: 'isActiveDowntime',
        Cell: ({ value }) => <span>{value || '-'}</span>,
      },
      {
        Header: 'Start',
        accessor: 'dateLocal',
        Cell: ({ value }) => <span>{formatDate(value, true)}</span>,
      },
      {
        Header: 'End',
        accessor: 'endDateLocal',
        Cell: ({ value }) => <span>{formatDate(value, true)}</span>,
      },
      {
        Header: 'Hours',
        accessor: 'hours',
        Cell: ({ value }) => <span>{value != null ? value.toFixed(2) : '-'}</span>,
      },
      {
        Header: 'Lost oil',
        accessor: 'offlineHours',
        Cell: ({ value }) => {
          return <span>{value === null || value === 0 ? '-' : value.toFixed(2)}</span>;
        },
      },
      {
        Header: 'Lost gas',
        accessor: 'currentHours',
        Cell: ({ value }) => {
          return <span>{value === null || value === 0 ? '-' : value.toFixed(2)}</span>;
        },
      },
      {
        Header: 'Lost water',
        accessor: 'lostWater',
        Cell: ({ value }) => <span>{value != null ? value.toFixed(2) : '-'}</span>,
      },
      {
        Header: 'Code',
        accessor: 'code',
        Cell: ({ value }) => <span>{value || '-'}</span>,
      },
      {
        Header: 'Category',
        accessor: 'category',
        Cell: ({ value }) => <span>{value || '-'}</span>,
      },
      {
        Header: 'Sub category',
        accessor: 'subCategory',
        Cell: ({ value }) => <span>{value || '-'}</span>,
      },
      {
        Header: 'Comment',
        accessor: 'comment',
        Cell: ({ value }) => <span>{value || '-'}</span>,
      },
    ],
    [],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: lastFive,
    },
    useSortBy,
  );

  return (
    <section className='table-container-wrapper mt-4 live-shutdown-wrapper'>
      <div className='table-container'>
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table
            {...getTableProps()}
            className='data-table task-detail-table center-text'
            style={{ minWidth: '1000px' }}
          >
            <thead>
              {headerGroups.map((headerGroup: HeaderGroup<any>) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((col: any) => {
                    const column = col as ColumnInstance<any> & UseSortByColumnProps<any>;
                    return (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className={`h-[44px] pt-[16px] pb-[16px] pl-[12px] pr-[12px] border-t border-[#4a5463] text-center col-${column.id}`}
                      >
                        <div className='flex gap-[4px] items-center task-detail justify-center'>
                          <span className='text-[12px] font-semibold text-[#f7f9f9]'>{column.render('Header')}</span>
                          {column.isSorted && (
                            <span
                              className={`tdt-sort-icon ${column.isSorted ? (column.isSortedDesc ? 'desc' : 'asc') : 'default'
                                }`}
                              style={{ display: column.render('Header') ? 'inline-block' : 'none' }}
                            />
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()} className='task-table-scroll'>
              {rows.length > 0 ? (
                rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  {columns.map((_, idx) => (
                    <td key={idx}>-</td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && (
          <div className='no-matching-records-container text-center p-4 pl-6 pr-6 h-[250px] flex items-center justify-center'>
            <span className='no-matching-records-message'>No Records found</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default LastFiveShutdownTable;
